'use client';

import React, { forwardRef, useMemo } from 'react';
import clsx from 'clsx';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import {
    getS3ObjectUrl,
    getStaticImageSource,
    getDynamicImageSource,
} from 'app/libs/url';
import i18n from '@shared/i18n';
import { FEATURE_FLAG_KEYS } from 'app/shop-ui/constants';
import { ProductCard } from './ProductCard';
import { ProductCardSkeleton } from './ProductCardSkeleton';
import './ProductCard.scss';
import { productGroupTypeKey } from '../ProductGroup/define';

const productCardRootClassNameMap = {
    [productGroupTypeKey.standard]: '',
    [productGroupTypeKey.feature]: '',
    [productGroupTypeKey.light]: 'product-card-list--light',
};

const defaultProduct = {
    name: i18n.gettext('原廠配件 | 日本BRUNO 電烤盤玻璃上蓋 (經典款、聯名款專用)'),
    price: {
        value: 'NT$ 1,480',
        currency: 'NT$',
        amount: '1480',
    },
    discountedPrice: {
        value: 'NT$ 0',
        currency: 'NT$',
        amount: '0',
    },
    source: [getStaticImageSource(getS3ObjectUrl(`/static/product-card-default_v2.png`))],
    alt: '',
};

export interface ProductCardListPropType extends BasicShopUIPropTypes, BuyerLayoutProps {
    onClick?: () => void;
    products: Product[];
    productCardOptions?: Partial<typeof defaultOptions>;
    isLoading: boolean;
}

const defaultOptions = {
    useButton: true,
    usePriceBadge: true,
    useShadow: true,
    useSquareImage: true,
    useProductName: true,
    useProductPrice: true,
    useProductBadge: true,
    buttonContent: i18n.gettext('我要加購'),
    buttonOnClick: () => {},
};

export const ProductCardList = forwardRef<HTMLDivElement, ProductCardListPropType>(
    (props, ref) => {
        const {
            type,
            style,
            styleMap,
            className,
            isEditingMode,
            onClick,
            isBuyer,
            products: originalProducts,
            productCardOptions = {},
            content: title,
            isLoading = false,
        } = props;

        const { products, options } = useMemo(() => {
            let _products = originalProducts;

            if (!styleMap) {
                return {
                    products: [],
                    options: {},
                };
            }

            const isFeatureType =
                styleMap[FEATURE_FLAG_KEYS.PRODUCT_CARD_TYPE] ===
                productGroupTypeKey.feature;

            const isLightType =
                styleMap[FEATURE_FLAG_KEYS.PRODUCT_CARD_TYPE] ===
                productGroupTypeKey.light;

            const usePriceBadge = isFeatureType;

            const useShadow = isLightType;

            const useSquareImage = isLightType;

            if ((!_products || _products.length === 0) && !isBuyer) {
                _products = [
                    defaultProduct,
                    defaultProduct,
                    defaultProduct,
                    defaultProduct,
                    defaultProduct,
                    defaultProduct,
                    defaultProduct,
                    defaultProduct,
                ] as unknown as Product[];
            }

            return {
                products: _products.map((product) => ({
                    source: [getDynamicImageSource(product.image?.src)],
                    ...product,
                })),
                options: {
                    ...defaultOptions,
                    ...productCardOptions,
                    usePriceBadge,
                    useShadow,
                    useSquareImage,
                    buttonContent: i18n.gettext('我要加購'),
                    buttonOnClick: onClick,
                },
            };
        }, [styleMap, originalProducts, isBuyer, onClick, productCardOptions]);

        const productCardRootClassName = styleMap
            ? productCardRootClassNameMap[styleMap[FEATURE_FLAG_KEYS.PRODUCT_CARD_TYPE]]
            : '';

        if (!products || products.length === 0) {
            return null;
        }

        return (
            <div
                ref={ref}
                className={clsx('product-card-list', productCardRootClassName, className)}
                style={style}
            >
                {title && <h2 className="product-card-list__title">{title}</h2>}
                <div
                    className={clsx(
                        'product-card-list__viewport',
                        isEditingMode && 'is-editing'
                    )}
                >
                    <div className="product-card__box">
                        {products.map((product, idx) => (
                            <div
                                key={`${product.name}_${idx}`}
                                className="product-card__container"
                            >
                                {isLoading ? (
                                    <ProductCardSkeleton />
                                ) : (
                                    <ProductCard
                                        {...product}
                                        {...options}
                                        componentType={type}
                                        isEditingMode={isEditingMode}
                                        isBuyer={isBuyer}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
);

ProductCardList.displayName = 'ProductCardList';
